import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { withRouter } from 'react-router-dom';
import Header from '../layout/header';
import { getLocalItem, getSessionItem, setSessionItem } from '../../utils/storage';
import { TERMINAL_STATUSES } from '../../constants/statuses';
import {
  WARRANTY,
  NEXT_SERVICE_SUGGEST_DAYS,
  SOCIAL_PROOF,
} from '../../constants/trust';

const API = 'https://ridenrepair.com/api';
const OTP_OK_KEY = 'passport_otp_ok'; // sessionStorage flag (per-phone), TTL via storage util

// ── helpers ──────────────────────────────────────────────────────────────────
const num = (v) => {
  const n = parseFloat(String(v ?? '').replace(/[^\d.]/g, ''));
  return isNaN(n) ? 0 : n;
};
const parseDate = (s) => {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};
const daysBetween = (a, b) => Math.floor((b - a) / 86400000);
const fmtDate = (d) =>
  d
    ? d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '';
const norm = (s) => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');

// Derived warranty state (computed policy layer — NO warranty data is stored).
function warrantyState(booking) {
  const base = parseDate(booking.date_of_service) || parseDate(booking.created_on);
  if (!base) return null;
  const today = new Date();
  const isEngine = /engine|overhaul/i.test(booking.service_type || '');
  const svcEnd = new Date(base.getTime() + WARRANTY.serviceDays * 86400000);
  const engEnd = new Date(base);
  engEnd.setMonth(engEnd.getMonth() + WARRANTY.engineMonths);
  const out = [];
  if (today <= svcEnd) {
    out.push({
      label: `Service warranty active · ${daysBetween(today, svcEnd)} days left`,
      active: true,
    });
  } else {
    out.push({ label: `Service warranty (${WARRANTY.serviceDays}-day) expired`, active: false });
  }
  if (isEngine) {
    out.push(
      today <= engEnd
        ? { label: `Engine warranty active · until ${fmtDate(engEnd)}`, active: true }
        : { label: `Engine warranty (${WARRANTY.engineMonths}-month) expired`, active: false }
    );
  }
  return out;
}

// Client-side GST invoice — mirrors the server IGST-18% formula EXACTLY, but
// renders NO GSTIN (GST differs per city; the legally-numbered invoice remains
// the emailed one). Opens a print-ready window.
function downloadInvoice(b) {
  const descs = String(b.charges_desc || '').split(',');
  const amts = String(b.amount_details || '').split(',');
  const rows = descs
    .map((d, i) => ({ d: d.trim(), a: num(amts[i]) }))
    .filter((r) => r.d);
  const total = rows.reduce((s, r) => s + r.a, 0);
  const excl = total / 1.18;
  const igst = total - excl;
  const money = (n) => `₹${n.toFixed(2)}`;
  const win = typeof window !== 'undefined' && window.open('', '_blank');
  if (!win) return;
  win.document.write(`<!doctype html><html><head><title>RNR Invoice ${b.booking_id}</title>
  <style>body{font-family:Arial,sans-serif;color:#111;padding:28px;max-width:720px;margin:auto}
  h1{color:#f42222;font-size:22px;margin:0 0 4px}.muted{color:#666;font-size:12px}
  table{width:100%;border-collapse:collapse;margin-top:18px}
  th,td{text-align:left;padding:8px;border-bottom:1px solid #eee;font-size:13px}
  td.r,th.r{text-align:right}tfoot td{font-weight:700}</style></head><body>
  <h1>Ride N Repair</h1><div class="muted">Doorstep vehicle service · GST invoice</div>
  <div style="margin-top:14px;font-size:13px">
  <strong>Booking ID:</strong> ${b.booking_id}<br/>
  <strong>Vehicle:</strong> ${b.vehicle_name || ''}<br/>
  <strong>Service:</strong> ${b.service_type || ''}<br/>
  <strong>Date:</strong> ${b.date_of_service || ''}</div>
  <table><thead><tr><th>Description</th><th class="r">Amount</th></tr></thead><tbody>
  ${rows.map((r) => `<tr><td>${r.d}</td><td class="r">${money(r.a)}</td></tr>`).join('')}
  </tbody><tfoot>
  <tr><td>Amount (Excl. IGST)</td><td class="r">${money(excl)}</td></tr>
  <tr><td>IGST (18%)</td><td class="r">${money(igst)}</td></tr>
  <tr><td>Total</td><td class="r">${money(total)}</td></tr>
  </tfoot></table>
  <p class="muted" style="margin-top:24px">This is a service record copy. Prices are
  inclusive of GST. For the official tax invoice, use “Email invoice” from your
  Passport.</p>
  <script>window.onload=function(){window.print()}</script></body></html>`);
  win.document.close();
}

const PassportSurface = ({ user, setUser, isMobileApp, history }) => {
  const [phone, setPhone] = useState('');
  const [profile, setProfile] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // OTP gate state (deterrence/UX — honestly NOT enforcement; the read
  // endpoints stay open. True enforcement is the approval-gated server endpoint.)
  const [needOtp, setNeedOtp] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpErr, setOtpErr] = useState('');

  // Resolve identity (Phase-2 hydration usually already did this).
  useEffect(() => {
    let p = user && user.ph ? user.ph : getLocalItem('userPhone');
    if (!p) {
      const raw = getLocalItem('userDetails');
      if (raw) {
        try {
          const d = JSON.parse(raw);
          p = d.phone;
        } catch (e) { /* ignore */ }
      }
    }
    if (!p) {
      history.push('/register');
      return;
    }
    setPhone(p);
    const verified = getSessionItem(`${OTP_OK_KEY}_${p}`) === 'yes';
    setNeedOtp(!verified);
  }, [user, history]);

  const loadData = useCallback(async (p) => {
    try {
      setLoading(true);
      const [pr, bk] = await Promise.all([
        axios.get(`${API}/get_user_profile?phone=${p}`),
        axios.get(`${API}/get_booking_from_phone?phone=${p}`),
      ]);
      setProfile(pr.data || null);
      setBookings(Array.isArray(bk.data) ? bk.data : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (phone && !needOtp) loadData(phone);
  }, [phone, needOtp, loadData]);

  const sendOtp = async () => {
    setOtpErr('');
    try {
      await axios.post(`${API}/send_booking_otp_sms`, { phone });
      setOtpSent(true);
    } catch (e) {
      setOtpErr('Could not send OTP. Please try again.');
    }
  };
  const verifyOtp = async () => {
    setOtpErr('');
    try {
      const res = await axios.post(
        `${API}/validate_booking_otp`,
        { phone, booking_otp: otp },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (res.data && res.data.is_valid) {
        setSessionItem(`${OTP_OK_KEY}_${phone}`, 'yes');
        setNeedOtp(false);
      } else {
        setOtpErr('Incorrect OTP.');
      }
    } catch (e) {
      setOtpErr('Verification failed. Please try again.');
    }
  };

  // Best-effort grouping by physical vehicle (no plate/VIN exists — honest).
  const groups = useMemo(() => {
    const m = new Map();
    bookings.forEach((b) => {
      const key =
        norm(b.vehicle_make) + '|' + norm(b.vehicle_model || b.vehicle_name);
      if (!m.has(key))
        m.set(key, { title: b.vehicle_name || b.vehicle_model || 'My Vehicle', items: [] });
      m.get(key).items.push(b);
    });
    return Array.from(m.values()).map((g) => {
      g.items.sort(
        (a, c) =>
          (parseDate(c.date_of_service) || 0) - (parseDate(a.date_of_service) || 0)
      );
      const last = parseDate(g.items[0] && g.items[0].date_of_service);
      g.nextDue = last
        ? new Date(last.getTime() + NEXT_SERVICE_SUGGEST_DAYS.default * 86400000)
        : null;
      return g;
    });
  }, [bookings]);

  const stats = useMemo(() => {
    const total = bookings.reduce((s, b) => s + num(b.final_payment_amount), 0);
    const active = bookings.reduce(
      (s, b) => s + ((warrantyState(b) || []).some((w) => w.active) ? 1 : 0),
      0
    );
    const years =
      bookings.length > 0
        ? bookings.reduce((min, b) => {
            const d = parseDate(b.date_of_service);
            return d && d < min ? d : min;
          }, new Date())
        : null;
    return {
      services: bookings.length,
      spent: Math.round(total),
      active,
      since: years ? years.getFullYear() : null,
    };
  }, [bookings]);

  const shareWhatsApp = () => {
    const txt = encodeURIComponent(
      `My vehicle service history is maintained on Ride N Repair — ${stats.services} services, ${stats.active} active warranties. ridenrepair.com/passport`
    );
    if (typeof window !== 'undefined')
      window.open(`https://wa.me/?text=${txt}`, '_blank');
  };

  return (
    <div className="rr-passport-parent">
      <Helmet>
        <title>My Service Passport — Ride N Repair</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Header user={user} setUser={setUser} isMobileApp={isMobileApp} />

      <div className="rr-passport">
        {needOtp ? (
          <div className="rr-passport__gate">
            <h1>Your Service Passport</h1>
            <p>For your security, verify your phone to view your service history.</p>
            <p className="rr-passport__phone">+91 {phone}</p>
            {!otpSent ? (
              <button className="rr-cta-primary" onClick={sendOtp}>
                Send OTP
              </button>
            ) : (
              <>
                <input
                  className="rr-passport__otp"
                  inputMode="numeric"
                  placeholder="Enter OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                />
                <button className="rr-cta-primary" onClick={verifyOtp}>
                  Verify &amp; open Passport
                </button>
                <button className="rr-passport__link" onClick={sendOtp}>
                  Resend OTP
                </button>
              </>
            )}
            {otpErr && <p className="rr-passport__err">{otpErr}</p>}
          </div>
        ) : loading ? (
          <div className="rr-passport__state">Loading your Passport…</div>
        ) : error ? (
          <div className="rr-passport__state">Couldn’t load: {error}</div>
        ) : (
          <>
            <header className="rr-passport__head">
              <div>
                <p className="rr-passport__kicker">Verified by Ride N Repair</p>
                <h1>
                  {profile && profile.first_name
                    ? `${profile.first_name}’s Service Passport`
                    : 'My Service Passport'}
                </h1>
              </div>
              <div className="rr-passport__actions">
                <button
                  className="rr-cta-secondary"
                  onClick={() => window.print()}
                >
                  Download passport
                </button>
                <button className="rr-cta-secondary" onClick={shareWhatsApp}>
                  Share
                </button>
              </div>
            </header>

            <div className="rr-passport__stats">
              <div><strong>{stats.services}</strong><span>services done</span></div>
              <div><strong>₹{stats.spent.toLocaleString('en-IN')}</strong><span>invested in care</span></div>
              <div><strong>{stats.active}</strong><span>active warranties</span></div>
              {stats.since && (
                <div><strong>{stats.since}</strong><span>with RNR since</span></div>
              )}
            </div>

            {!isMobileApp && (
              <div className="rr-passport__app">
                Keep your full service history &amp; GST invoices in your pocket —
                get the Ride N Repair app.
              </div>
            )}

            {groups.length === 0 ? (
              <div className="rr-passport__state">
                No services yet. Your verified service history will appear here
                after your first Ride N Repair service — backed by a{' '}
                {WARRANTY.serviceDays}-day warranty and genuine parts.
              </div>
            ) : (
              groups.map((g, gi) => (
                <section className="rr-passport__vehicle" key={gi}>
                  <div className="rr-passport__vehicle-head">
                    <h2>{g.title}</h2>
                    {g.nextDue && (
                      <span className="rr-passport__due">
                        Suggested next service around {fmtDate(g.nextDue)}
                      </span>
                    )}
                  </div>

                  {g.items.map((b, bi) => {
                    const w = warrantyState(b) || [];
                    const descs = String(b.charges_desc || '').split(',');
                    const amts = String(b.amount_details || '').split(',');
                    const active =
                      !TERMINAL_STATUSES.includes(b.order_status) &&
                      b.mechanic_number;
                    return (
                      <article className="rr-passport__svc" key={bi}>
                        <div className="rr-passport__svc-top">
                          <div>
                            <h3>{b.service_type || 'Service'}</h3>
                            <p className="rr-passport__meta">
                              {b.date_of_service}
                              {b.mechanic_name ? ` · ${b.mechanic_name}` : ''} ·
                              Booking #{b.booking_id}
                            </p>
                          </div>
                          {b.final_payment_amount ? (
                            <div className="rr-passport__amt">
                              ₹{num(b.final_payment_amount).toLocaleString('en-IN')}
                            </div>
                          ) : null}
                        </div>

                        {w.length > 0 && (
                          <div className="rr-passport__chips">
                            {w.map((x, xi) => (
                              <span
                                key={xi}
                                className={`rr-chip ${
                                  x.active ? 'rr-chip--ok' : 'rr-chip--off'
                                }`}
                              >
                                {x.label}
                              </span>
                            ))}
                          </div>
                        )}

                        {b.charges_desc && b.amount_details && (
                          <details className="rr-passport__items">
                            <summary>Work done &amp; parts</summary>
                            <ul>
                              {descs.map((d, i) =>
                                d.trim() ? (
                                  <li key={i}>
                                    <span>{d.trim()}</span>
                                    <span>
                                      ₹{(amts[i] || '').trim()}
                                    </span>
                                  </li>
                                ) : null
                              )}
                            </ul>
                          </details>
                        )}

                        <div className="rr-passport__svc-actions">
                          {(b.order_status === 'COMPLETED' ||
                            b.order_status === 'CASH TAKEN') && (
                            <button
                              className="rr-passport__link"
                              onClick={() => downloadInvoice(b)}
                            >
                              Download GST invoice
                            </button>
                          )}
                          {active && (
                            <button
                              className="rr-passport__link"
                              onClick={() =>
                                history.push({
                                  pathname: '/booking-details',
                                  search: `actual_booking_id=${b.booking_id}`,
                                })
                              }
                            >
                              Track this service
                            </button>
                          )}
                        </div>
                      </article>
                    );
                  })}
                </section>
              ))
            )}

            <p className="rr-passport__foot">
              {SOCIAL_PROOF.customers} customers trust Ride N Repair · genuine
              spare parts only · GST invoice on every service
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default withRouter(PassportSurface);
